import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { z } from 'zod';
import { mediaHttp } from '@/lib/api/http-client';
import { parseResponse } from '@/lib/api/parse-response';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { useCampaignInvalidation } from './campaign-mutations';

export interface ImportDeliveryFileInput {
  file: File;
  /** Names the person confirmed should become new campaigns. */
  createCampaigns?: string[];
}

const importedSchema = z.looseObject({
  ingestRunId: z.string(),
  rowsWritten: z.number(),
  windowStart: z.string(),
  windowEnd: z.string(),
  ignoredColumns: z.array(z.string()),
  clearedColumns: z.array(z.string()),
  createdCampaigns: z.array(z.looseObject({ id: z.string(), name: z.string() })),
  platformNotices: z
    .array(
      z.looseObject({
        campaignName: z.string(),
        code: z.string(),
        labels: z.array(z.string()),
        keptPlatform: z.string().optional(),
      }),
    )
    .default([]),
});

const refusalSchema = z.looseObject({
  code: z.literal('delivery_file_invalid'),
  errors: z.array(
    z.looseObject({
      row: z.number().nullable(),
      column: z.string().nullable(),
      code: z.string(),
      message: z.string(),
    }),
  ),
  campaignsToCreate: z.array(z.looseObject({ name: z.string(), rows: z.number() })),
});

export type PlatformNotice = z.infer<typeof importedSchema>['platformNotices'][number];
export type DeliveryFileProblem = z.infer<typeof refusalSchema>['errors'][number];
export type CampaignToCreate = z.infer<typeof refusalSchema>['campaignsToCreate'][number];

export type DeliveryImportOutcome =
  | ({ kind: 'imported' } & z.infer<typeof importedSchema>)
  | {
      kind: 'refused';
      problems: DeliveryFileProblem[];
      campaignsToCreate: CampaignToCreate[];
    }
  | { kind: 'failed'; status: number | undefined; code: string | undefined; detail: string };

/**
 * Uploads a delivery file for a project. A refusal is the normal first answer for a file that
 * names new campaigns, so it comes back as an outcome rather than an error, and the dialog
 * handles every status itself instead of the global toast.
 */
export function useImportDeliveryFile(projectId: string) {
  const invalidate = useCampaignInvalidation(projectId);

  return useMutation({
    mutationFn: async ({
      file,
      createCampaigns,
    }: ImportDeliveryFileInput): Promise<DeliveryImportOutcome> => {
      const form = new FormData();
      form.append('file', file);
      form.append('projectId', projectId);
      if (createCampaigns) form.append('createCampaigns', JSON.stringify(createCampaigns));
      try {
        const res = await mediaHttp.post({
          security: [{ scheme: 'bearer', type: 'http' }],
          url: '/api/v1/media-delivery-imports',
          body: form,
          headers: {
            // A key per send: the confirmation is a new request with a different body.
            'Idempotency-Key': crypto.randomUUID(),
            // eslint-disable-next-line i18next/no-literal-string -- HTTP header value
            [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true',
          },
        });
        return { kind: 'imported', ...parseResponse(importedSchema, res) };
      } catch (error) {
        const refusal = refusalSchema.safeParse(
          isAxiosError(error) ? error.response?.data : undefined,
        );
        if (refusal.success) {
          return {
            kind: 'refused',
            problems: refusal.data.errors,
            campaignsToCreate: refusal.data.campaignsToCreate,
          };
        }
        return { kind: 'failed', ...parseProblemDetail(error, '') };
      }
    },
    // Not awaited: the result step should not wait for every performance window to refetch.
    onSuccess: (outcome) => {
      if (outcome.kind === 'imported') void invalidate();
    },
  });
}
